import {
    AIConvoThinkingParams,
    AIConvoThinkingStep,
    AIConvoThinkingTense,
    AIConvoThinkingTool,
} from '@modules/ai-convo/models/ai-convo-thinking.model';

const SEMANTIC_QUERY_MAX_LENGTH = 60;

export const AI_CONVO_THINKING_GENERIC_KEYS: readonly string[] = [
    'AI.THINKING.GENERIC.ANALYZING_REQUEST',
    'AI.THINKING.GENERIC.PROCESSING_INFORMATION',
    'AI.THINKING.GENERIC.PREPARING_ANSWER',
    'AI.THINKING.GENERIC.TAKING_LONGER',
    'AI.THINKING.GENERIC.VERIFYING_DETAILS',
];

export type AIConvoThinkingLabel = {
    key: string;
    params: Record<string, string>;
};

const KEYS = {
    SEMANTIC_SEARCH: {
        present: 'AI.THINKING.PRESENT.SEMANTIC_SEARCH',
        past: 'AI.THINKING.PAST.SEMANTIC_SEARCH',
    },
    SEMANTIC_SEARCH_NO_QUERY: {
        present: 'AI.THINKING.PRESENT.SEMANTIC_SEARCH_NO_QUERY',
        past: 'AI.THINKING.PAST.SEMANTIC_SEARCH_NO_QUERY',
    },
    LIST_COMMUNITIES: {
        present: 'AI.THINKING.PRESENT.LIST_COMMUNITIES',
        past: 'AI.THINKING.PAST.LIST_COMMUNITIES',
    },
    COMMUNITY_INFO: {
        present: 'AI.THINKING.PRESENT.COMMUNITY_INFO',
        past: 'AI.THINKING.PAST.COMMUNITY_INFO',
    },
    COMMUNITY_INFO_NO_NAME: {
        present: 'AI.THINKING.PRESENT.COMMUNITY_INFO_NO_NAME',
        past: 'AI.THINKING.PAST.COMMUNITY_INFO_NO_NAME',
    },
    READ_POST: {
        present: 'AI.THINKING.PRESENT.READ_POST',
        past: 'AI.THINKING.PAST.READ_POST',
    },
    READ_POST_NO_TITLE: {
        present: 'AI.THINKING.PRESENT.READ_POST_NO_TITLE',
        past: 'AI.THINKING.PAST.READ_POST_NO_TITLE',
    },
    SEARCH_POSTS_WITH_TEXT_IN_COMMUNITY: {
        present: 'AI.THINKING.PRESENT.SEARCH_POSTS_WITH_TEXT_IN_COMMUNITY',
        past: 'AI.THINKING.PAST.SEARCH_POSTS_WITH_TEXT_IN_COMMUNITY',
    },
    SEARCH_POSTS_IN_COMMUNITY: {
        present: 'AI.THINKING.PRESENT.SEARCH_POSTS_IN_COMMUNITY',
        past: 'AI.THINKING.PAST.SEARCH_POSTS_IN_COMMUNITY',
    },
    SEARCH_POSTS_WITH_TEXT: {
        present: 'AI.THINKING.PRESENT.SEARCH_POSTS_WITH_TEXT',
        past: 'AI.THINKING.PAST.SEARCH_POSTS_WITH_TEXT',
    },
    SEARCH_POSTS: {
        present: 'AI.THINKING.PRESENT.SEARCH_POSTS',
        past: 'AI.THINKING.PAST.SEARCH_POSTS',
    },
    ANALYZE_ATTACHMENT: {
        present: 'AI.THINKING.PRESENT.ANALYZE_ATTACHMENT',
        past: 'AI.THINKING.PAST.ANALYZE_ATTACHMENT',
    },
    ANALYZE_ATTACHMENT_NO_NAME: {
        present: 'AI.THINKING.PRESENT.ANALYZE_ATTACHMENT_NO_NAME',
        past: 'AI.THINKING.PAST.ANALYZE_ATTACHMENT_NO_NAME',
    },
} as const satisfies Record<string, Record<AIConvoThinkingTense, string>>;

type ThinkingVariant = keyof typeof KEYS;

function truncate(text: string, maxLength: number): string {
    return text.length > maxLength
        ? `${text.slice(0, maxLength).trimEnd()}…`
        : text;
}

function present(value: string | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}

function getVariant(
    tool: AIConvoThinkingTool,
    params: AIConvoThinkingParams,
): { variant: ThinkingVariant; params: Record<string, string> } | null {
    const query = present(params.query);
    const communityName = present(params.communityName);
    const postTitle = present(params.postTitle);
    const text = present(params.text);
    const fileName = present(params.fileName);

    switch (tool) {
        case AIConvoThinkingTool.SEMANTIC_SEARCH:
            return query
                ? {
                      variant: 'SEMANTIC_SEARCH',
                      params: {
                          query: truncate(query, SEMANTIC_QUERY_MAX_LENGTH),
                      },
                  }
                : { variant: 'SEMANTIC_SEARCH_NO_QUERY', params: {} };
        case AIConvoThinkingTool.LIST_COMMUNITIES:
            return { variant: 'LIST_COMMUNITIES', params: {} };
        case AIConvoThinkingTool.COMMUNITY_INFO:
            return communityName
                ? { variant: 'COMMUNITY_INFO', params: { communityName } }
                : { variant: 'COMMUNITY_INFO_NO_NAME', params: {} };
        case AIConvoThinkingTool.READ_POST:
            return postTitle
                ? { variant: 'READ_POST', params: { postTitle } }
                : { variant: 'READ_POST_NO_TITLE', params: {} };
        case AIConvoThinkingTool.SEARCH_POSTS:
            if (communityName && text) {
                return {
                    variant: 'SEARCH_POSTS_WITH_TEXT_IN_COMMUNITY',
                    params: { communityName, text },
                };
            }
            if (communityName) {
                return {
                    variant: 'SEARCH_POSTS_IN_COMMUNITY',
                    params: { communityName },
                };
            }
            return text
                ? { variant: 'SEARCH_POSTS_WITH_TEXT', params: { text } }
                : { variant: 'SEARCH_POSTS', params: {} };
        case AIConvoThinkingTool.ANALYZE_ATTACHMENT:
            return fileName
                ? { variant: 'ANALYZE_ATTACHMENT', params: { fileName } }
                : { variant: 'ANALYZE_ATTACHMENT_NO_NAME', params: {} };
        default:
            return null;
    }
}

/**
 * The phrase of a step, by the parameters it carries: present tense while the
 * agent is working, past tense in the list under the answer. A tool the FE does
 * not know has no phrase of its own.
 */
export function getThinkingLabel(
    step: AIConvoThinkingStep,
    tense: AIConvoThinkingTense,
): AIConvoThinkingLabel | null {
    const resolved = getVariant(step.tool, step.params);
    if (!resolved) return null;

    return {
        key: KEYS[resolved.variant][tense],
        params: resolved.params,
    };
}
